import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, database } from './components/firebaseConfig';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/Dashboard';
import AdminAnalytics from './components/AdminAnalytics';
import Hakkimizda from './components/Hakkimizda';
import GizlilikPolitikasi from './components/GizlilikPolitikasi';
import KullanimKosullari from './components/KullanimKosullari';
import MesafeliSatisSozlesmesi from './components/MesafeliSatisSozlesmesi';
import TeslimatIade from './components/TeslimatIade';
import PaymentCallback from './components/PaymentCallback';
import useEnterNavigation from './hooks/useEnterNavigation';
// Kurumsal Müşteri Sayfaları
import KurumsalLogin from './components/KurumsalLogin';
import KurumsalPuantajlar from './components/KurumsalPuantajlar';

// Eski yönetim paneli ayrı chunk olarak yüklenir (CSS'i /admin analitiğine sızmasın)
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

function App() {
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isCorporateUser, setIsCorporateUser] = useState(false); // Kurumsal kullanıcı kontrolü

  // Global Enter tuşu navigasyonu
  useEnterNavigation(true);

  useEffect(() => {
    let unsubscribe;

    /** Landing → Webapp SSO: URL fragment (#fbAuth=) ile gelen Firebase custom token */
    const consumeSsoToken = async () => {
      if (typeof window === 'undefined' || !window.location.hash) return;
      const params = new URLSearchParams(window.location.hash.slice(1));
      const token = params.get('fbAuth');
      if (!token) return;

      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );

      try {
        await setPersistence(auth, browserSessionPersistence);
        await signInWithCustomToken(auth, token);
      } catch (err) {
        console.error('SSO bridge sign-in başarısız:', err);
      }
    };

    const initAuth = async () => {
      await consumeSsoToken();
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          setUser(user);
          setUserEmail(user.email);

          // Kurumsal kullanıcı kontrolü
          try {
            const corporateUserRef = doc(database, 'corporateUsers', user.email);
            const corporateUserDoc = await getDoc(corporateUserRef);
            setIsCorporateUser(corporateUserDoc.exists());
          } catch (error) {
            console.error('Kurumsal kullanıcı kontrolü hatası:', error);
            setIsCorporateUser(false);
          }
        } else {
          setUser(null);
          setUserEmail('');
          setIsCorporateUser(false);
        }
        setLoading(false);
      });
    };

    initAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleLogin = (email) => {
    setUserEmail(email);
  };

  const handleLogout = () => {
    setUserEmail('');
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            user ? (
              // Kurumsal kullanıcıyı kurumsal panele yönlendir
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Login setEmail={setUserEmail} onLogin={handleLogin} />
            )
          } 
        />
        <Route 
          path="/signup" 
          element={
            user ? (
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Signup />
            )
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            user ? (
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <ForgotPassword />
            )
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            user ? (
              // Kurumsal kullanıcı dashboard'a erişemez
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <Dashboard onSignOut={handleLogout} userEmail={userEmail} />
              )
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/admin" 
          element={
            user ? (
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <AdminAnalytics onSignOut={handleLogout} userEmail={userEmail} />
              )
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route
          path="/admin/legacy"
          element={
            user ? (
              isCorporateUser ? (
                <Navigate to="/kurumsal/puantajlar" replace />
              ) : (
                <Suspense fallback={<div style={{ padding: 40 }}>Yükleniyor...</div>}>
                  <AdminDashboard onSignOut={handleLogout} userEmail={userEmail} />
                </Suspense>
              )
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/hakkimizda" element={<Hakkimizda />} />
        <Route path="/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
        <Route path="/kullanim-kosullari" element={<KullanimKosullari />} />
        <Route path="/mesafeli-satis-sozlesmesi" element={<MesafeliSatisSozlesmesi />} />
        <Route path="/teslimat-iade" element={<TeslimatIade />} />
        <Route path="/payment/success" element={<PaymentCallback />} />
        <Route path="/payment/fail" element={<PaymentCallback />} />
        {/* Kurumsal Müşteri Sayfaları */}
        <Route path="/kurumsal" element={<KurumsalLogin />} />
        <Route path="/kurumsal/puantajlar" element={<KurumsalPuantajlar />} />
      </Routes>
    </Router>
  );
}

export default App;
